import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { EntityProvider } from './context/EntityContext';
import { PageTitleProvider } from './context/PageTitleContext';
import LoadingState from './components/ui/LoadingState';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ShareViewPage from './pages/ShareViewPage';
import { MULTI_ENTITY_ENABLED } from './config/features';
import RequireModule from './components/RequireModule';
import PwaShell from './components/PwaShell';
import WelcomePage from './pages/WelcomePage';
import PortalShell from './layouts/PortalShell';
import { useModules } from './context/ModulesContext';
import { firstEnabledCrmPath } from './config/modules';
import { homePathForUser, isTesoreroUser, welcomeShownKey } from './config/roles';
import { useAuth } from './context/AuthContext';

const AdminShell = lazy(() => import('./layouts/AdminShell'));
const CrmLayout = lazy(() => import('./layouts/CrmLayout'));
const AccountingLayout = lazy(() => import('./layouts/AccountingLayout'));
const ConfigLayout = lazy(() => import('./layouts/ConfigLayout'));

const TesoreriaHubPage = lazy(() => import('./pages/admin/TesoreriaHubPage'));
const InventarioHubPage = lazy(() => import('./pages/admin/InventarioHubPage'));
const MasPage = lazy(() => import('./pages/admin/MasPage'));
const MovimientosPage = lazy(() => import('./pages/admin/MovimientosPage'));
const FacturasVentaPage = lazy(() => import('./pages/admin/FacturasVentaPage'));
const PresupuestosIndexPage = lazy(() => import('./pages/admin/PresupuestosIndexPage'));
const BalancePage = lazy(() => import('./pages/admin/BalancePage'));
const RefacturacionPage = lazy(() => import('./pages/admin/RefacturacionPage'));
const BanksListPage = lazy(() => import('./pages/admin/accounting/BanksListPage'));
const InventarioMaterialPage = lazy(() => import('./pages/admin/inventario/InventarioMaterialPage'));
const InventarioMovimientosPage = lazy(() => import('./pages/admin/inventario/InventarioMovimientosPage'));
const InventarioConfigPage = lazy(() => import('./pages/admin/inventario/InventarioConfigPage'));
const InventarioReservasPage = lazy(() => import('./pages/admin/inventario/InventarioReservasPage'));
const EntidadesPage = lazy(() => import('./pages/admin/config/EntidadesPage'));
const PartidasPage = lazy(() => import('./pages/admin/config/PartidasPage'));
const PlantillaPresupuestoPage = lazy(() => import('./pages/admin/config/PlantillaPresupuestoPage'));
const EjerciciosPage = lazy(() => import('./pages/admin/config/EjerciciosPage'));
const BuzonEmailPage = lazy(() => import('./pages/admin/config/BuzonEmailPage'));
const ParametrizacionPage = lazy(() => import('./pages/admin/config/ParametrizacionPage'));
const PortalHomePage = lazy(() => import('./pages/portal/PortalHomePage'));
const EstadoContablePage = lazy(() => import('./pages/portal/EstadoContablePage'));
const BecasPage = lazy(() => import('./pages/portal/BecasPage'));
const PresupuestosPortalPage = lazy(() => import('./pages/portal/PresupuestosPortalPage'));
const PresupuestoCampamentoPage = lazy(() => import('./pages/portal/PresupuestoCampamentoPage'));
const FacturasCompraPage = lazy(() => import('./pages/admin/FacturasCompraPage'));
const JustificantesPage = lazy(() => import('./pages/admin/JustificantesPage'));
const ProyectosPage = lazy(() => import('./pages/admin/ProyectosPage'));
const ProyectoDetailPage = lazy(() => import('./pages/admin/ProyectoDetailPage'));
const SubvencionesPage = lazy(() => import('./pages/admin/SubvencionesPage'));
const SubvencionDetailPage = lazy(() => import('./pages/admin/SubvencionDetailPage'));
const ScholarshipProgramsPage = lazy(() => import('./pages/admin/ScholarshipProgramsPage'));
const ScholarshipProgramDetailPage = lazy(() => import('./pages/admin/ScholarshipProgramDetailPage'));
const PresupuestoFiscalYearPage = lazy(() => import('./pages/admin/PresupuestoFiscalYearPage'));
const GestorArchivosPage = lazy(() => import('./pages/admin/GestorArchivosPage'));
const BankDetailPage = lazy(() => import('./pages/admin/accounting/BankDetailPage'));
const CrmListPage = lazy(() => import('./pages/admin/crm/CrmListPage'));
const CrmContactDetailPage = lazy(() => import('./pages/admin/crm/CrmContactDetailPage'));
const CrmUsuariosPage = lazy(() => import('./pages/admin/crm/CrmUsuariosPage'));
const CrmPersonasPage = lazy(() => import('./pages/admin/crm/CrmPersonasPage'));
const CrmPersonaDetailPage = lazy(() => import('./pages/admin/crm/CrmPersonaDetailPage'));
const GruposScoutPage = lazy(() => import('./pages/admin/crm/GruposScoutPage'));
const GrupoScoutDetailPage = lazy(() => import('./pages/admin/crm/GrupoScoutDetailPage'));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState variant="page" />}>{children}</Suspense>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('tesoreria_token') : null;
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function HomeRedirect() {
  const { userId, entities, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  try {
    if (userId != null && !sessionStorage.getItem(welcomeShownKey(userId))) {
      return <Navigate to="/welcome" replace />;
    }
  } catch {
    /* ignore */
  }
  return <Navigate to={homePathForUser(entities)} replace />;
}

function RequireStaff({ children }: { children: React.ReactNode }) {
  const { entities } = useAuth();
  if (isTesoreroUser(entities)) return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

function RequireTesorero({ children }: { children: React.ReactNode }) {
  const { entities } = useAuth();
  if (!isTesoreroUser(entities)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function CrmIndexRedirect() {
  const { isModuleEnabled, loading } = useModules();
  if (loading) return <LoadingState variant="page" />;
  return <Navigate to={firstEnabledCrmPath(isModuleEnabled)} replace />;
}

function CrmTypeGate() {
  const { type } = useParams();
  const { isModuleEnabled, loading } = useModules();
  if (loading) return <LoadingState variant="page" />;
  if (type === 'clientes' && !isModuleEnabled('clientes')) return <Navigate to="/admin/tesoreria" replace />;
  if (type === 'proveedores' && !isModuleEnabled('proveedores')) return <Navigate to="/admin/tesoreria" replace />;
  if (type === 'personas' && !isModuleEnabled('becas')) return <Navigate to="/admin/tesoreria" replace />;
  return <LazyPage><CrmListPage /></LazyPage>;
}

function CrmTypeDetailGate() {
  const { type } = useParams();
  const { isModuleEnabled, loading } = useModules();
  if (loading) return <LoadingState variant="page" />;
  if (type === 'clientes' && !isModuleEnabled('clientes')) return <Navigate to="/admin/tesoreria" replace />;
  if (type === 'proveedores' && !isModuleEnabled('proveedores')) return <Navigate to="/admin/tesoreria" replace />;
  if (type === 'personas' && !isModuleEnabled('becas')) return <Navigate to="/admin/tesoreria" replace />;
  return <LazyPage><CrmContactDetailPage /></LazyPage>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <EntityProvider>
          <PageTitleProvider>
          <PwaShell />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/share/:token" element={<ShareViewPage />} />
            <Route path="/welcome" element={<RequireAuth><WelcomePage /></RequireAuth>} />
            <Route path="/" element={<RequireAuth><HomeRedirect /></RequireAuth>} />
            <Route
              path="portal"
              element={
                <RequireAuth>
                  <RequireTesorero>
                    <PortalShell />
                  </RequireTesorero>
                </RequireAuth>
              }
            >
              <Route index element={<LazyPage><PortalHomePage /></LazyPage>} />
              <Route path="estado-contable" element={<LazyPage><EstadoContablePage /></LazyPage>} />
              <Route path="becas" element={<LazyPage><BecasPage /></LazyPage>} />
              <Route path="presupuestos" element={<LazyPage><PresupuestosPortalPage /></LazyPage>} />
              <Route path="presupuestos/:id" element={<LazyPage><PresupuestoCampamentoPage /></LazyPage>} />
            </Route>
            <Route
              path="admin"
              element={
                <RequireAuth>
                  <RequireStaff>
                    <LazyPage><AdminShell /></LazyPage>
                  </RequireStaff>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/admin/tesoreria" replace />} />
              <Route path="tesoreria" element={<LazyPage><TesoreriaHubPage /></LazyPage>} />
              <Route path="inventario" element={<RequireModule module="inventario"><LazyPage><InventarioHubPage /></LazyPage></RequireModule>} />
              <Route path="inventario/material" element={<RequireModule module="inventario"><LazyPage><InventarioMaterialPage /></LazyPage></RequireModule>} />
              <Route path="inventario/reservas" element={<RequireModule module="inventario"><LazyPage><InventarioReservasPage /></LazyPage></RequireModule>} />
              <Route path="inventario/movimientos" element={<RequireModule module="inventario"><LazyPage><InventarioMovimientosPage /></LazyPage></RequireModule>} />
              <Route path="inventario/config" element={<RequireModule module="inventario"><LazyPage><InventarioConfigPage /></LazyPage></RequireModule>} />
              <Route path="crm" element={<LazyPage><CrmLayout /></LazyPage>}>
                <Route index element={<CrmIndexRedirect />} />
                <Route
                  path="grupos-scout"
                  element={<RequireModule module="grupos_scout"><LazyPage><GruposScoutPage /></LazyPage></RequireModule>}
                />
                <Route
                  path="grupos-scout/:id"
                  element={<RequireModule module="grupos_scout"><LazyPage><GrupoScoutDetailPage /></LazyPage></RequireModule>}
                />
                <Route
                  path="personas"
                  element={<RequireModule module="becas"><LazyPage><CrmPersonasPage /></LazyPage></RequireModule>}
                />
                <Route
                  path="personas/:id"
                  element={<RequireModule module="becas"><LazyPage><CrmPersonaDetailPage /></LazyPage></RequireModule>}
                />
                <Route path="acreedores" element={<Navigate to="/admin/crm/proveedores" replace />} />
                <Route path="acreedores/:id" element={<Navigate to="/admin/crm/proveedores" replace />} />
                <Route path="usuarios" element={<LazyPage><CrmUsuariosPage /></LazyPage>} />
                <Route path=":type" element={<CrmTypeGate />} />
                <Route path=":type/:id" element={<CrmTypeDetailGate />} />
              </Route>
              <Route path="bancos" element={<Navigate to="/admin/accounting/banks" replace />} />
              <Route path="accounting" element={<RequireModule module="bancos"><LazyPage><AccountingLayout /></LazyPage></RequireModule>}>
                <Route index element={<Navigate to="banks" replace />} />
                <Route path="banks" element={<LazyPage><BanksListPage /></LazyPage>} />
                <Route path="banks/:bankSlug" element={<LazyPage><BankDetailPage /></LazyPage>} />
                <Route path="banks/:bankSlug/suggest-reconciliations" element={<Navigate to=".." relative="path" replace />} />
                <Route path="reconciliation-rules" element={<Navigate to="/admin/accounting/banks" replace />} />
              </Route>
              <Route path="balance" element={<RequireModule module="informes"><LazyPage><BalancePage /></LazyPage></RequireModule>} />
              <Route path="files" element={<RequireModule module="archivos"><LazyPage><GestorArchivosPage /></LazyPage></RequireModule>} />
              <Route path="facturas-compra" element={<RequireModule module="facturas_compra"><LazyPage><FacturasCompraPage /></LazyPage></RequireModule>} />
              <Route path="facturas-venta" element={<RequireModule module="facturas_venta"><LazyPage><FacturasVentaPage /></LazyPage></RequireModule>} />
              <Route path="justificantes" element={<RequireModule module="justificantes"><LazyPage><JustificantesPage /></LazyPage></RequireModule>} />
              <Route path="movimientos" element={<RequireModule module="movimientos"><LazyPage><MovimientosPage /></LazyPage></RequireModule>} />
              <Route
                path="refacturaciones"
                element={MULTI_ENTITY_ENABLED ? <RequireModule module="refacturaciones"><LazyPage><RefacturacionPage /></LazyPage></RequireModule> : <Navigate to="/admin/tesoreria" replace />}
              />
              <Route path="proyectos" element={<RequireModule module="proyectos"><LazyPage><ProyectosPage /></LazyPage></RequireModule>} />
              <Route path="proyectos/:id" element={<RequireModule module="proyectos"><LazyPage><ProyectoDetailPage /></LazyPage></RequireModule>} />
              <Route path="subvenciones" element={<RequireModule module="subvenciones"><LazyPage><SubvencionesPage /></LazyPage></RequireModule>} />
              <Route path="subvenciones/:id" element={<RequireModule module="subvenciones"><LazyPage><SubvencionDetailPage /></LazyPage></RequireModule>} />
              <Route path="becas" element={<RequireModule module="becas"><LazyPage><ScholarshipProgramsPage /></LazyPage></RequireModule>} />
              <Route path="becas/:id" element={<RequireModule module="becas"><LazyPage><ScholarshipProgramDetailPage /></LazyPage></RequireModule>} />
              <Route path="presupuestos" element={<RequireModule module="presupuesto"><LazyPage><PresupuestosIndexPage /></LazyPage></RequireModule>} />
              <Route path="presupuesto" element={<Navigate to="/admin/presupuestos" replace />} />
              <Route path="presupuesto/:fiscal_year_id" element={<RequireModule module="presupuesto"><LazyPage><PresupuestoFiscalYearPage /></LazyPage></RequireModule>} />
              <Route path="mas" element={<LazyPage><MasPage /></LazyPage>} />
              <Route path="config" element={<LazyPage><ConfigLayout /></LazyPage>}>
                <Route index element={<Navigate to="ejercicios" replace />} />
                <Route path="entidades" element={<LazyPage><EntidadesPage /></LazyPage>} />
                <Route path="proveedores" element={<Navigate to="/admin/crm/proveedores" replace />} />
                <Route path="partidas" element={<LazyPage><PartidasPage /></LazyPage>} />
                <Route path="plantilla-presupuesto" element={<LazyPage><PlantillaPresupuestoPage /></LazyPage>} />
                <Route path="ejercicios" element={<LazyPage><EjerciciosPage /></LazyPage>} />
                <Route path="buzon" element={<LazyPage><BuzonEmailPage /></LazyPage>} />
                <Route path="parametrizacion" element={<LazyPage><ParametrizacionPage /></LazyPage>} />
                <Route path="usuarios" element={<Navigate to="/admin/crm/usuarios" replace />} />
              </Route>
            </Route>
          </Routes>
          </PageTitleProvider>
        </EntityProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
